import MarkdownIt from 'markdown-it'
import type {Mermaid, MermaidConfig} from 'mermaid'
import {sanitizeHtmlOnly, sanitizeRichHtml, sanitizeSvgOnly} from '@/lib/htmlSanitizer'
import {createButtonGroup} from './copyButton'

// Base64 encoding/decoding available in browser environment
const base64Encode = (str: string): string => {
  return btoa(unescape(encodeURIComponent(str)))
}

const base64Decode = (str: string): string => {
  return decodeURIComponent(escape(atob(str)))
}

// Default Mermaid configuration
let _config: MermaidConfig = {
  theme: 'default',
  fontFamily: 'Inconsolata, monospace !important',
  startOnLoad: false,
  logLevel: 'fatal',
  // NOTE: HTML tags in text are allowed and click functionality is enabled.
  // https://mermaid.js.org/config/usage.html#securitylevel
  securityLevel: 'strict',
  suppressErrorRendering: true,
}

let mermaidInstance: Mermaid | null = null
let mermaidInitPromise: Promise<Mermaid> | null = null
let iconPackRegistered = false

// Cache for memoization
const renderCache = new Map<string, string>()

// Simple hash function for code content
const hashCode = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash &= hash
  }
  return hash.toString(36)
}

const getHashCodeId = (code: string): string => {
  const codeHash = hashCode(code)
  return `mermaid-${codeHash}`
}

const addMermaidButtons = (container: HTMLElement, code: string) => {
  const getSvgBlob = () => {
    const svg = container.querySelector('svg')
    if (!svg) return new Blob([''], { type: 'image/svg+xml' })
    const svgData = new XMLSerializer().serializeToString(svg)
    return new Blob([svgData], { type: 'image/svg+xml' })
  }

  const group = createButtonGroup([
    {
      icon: 'copy',
      title: 'Copy source',
      onClick: async () => {
        await navigator.clipboard.writeText(code)
      },
    },
    {
      icon: 'download',
      title: 'Download SVG',
      onClick: () => {
        const blob = getSvgBlob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'diagram.svg'
        a.click()
        URL.revokeObjectURL(url)
      },
    },
  ])
  container.appendChild(group)
}

const ensureMermaid = async (): Promise<Mermaid> => {
  if (mermaidInstance) return mermaidInstance

  if (!mermaidInitPromise) {
    mermaidInitPromise = (async () => {
      const mod = await import('mermaid')
      const mermaid = mod.default

      mermaid.initialize(_config)

      if (!iconPackRegistered) {
        mermaid.registerIconPacks([
          {
            name: 'logos',
            loader: () =>
              fetch('https://unpkg.com/@iconify-json/logos@1/icons.json').then((res) => res.json()),
          },
        ])
        iconPackRegistered = true
      }

      mermaidInstance = mermaid
      return mermaid
    })().catch((error) => {
      mermaidInitPromise = null
      throw error
    })
  }

  return mermaidInitPromise
}

const renderAndCaching = async (id: string, code: string) => {
  const containerElem = document.getElementById(id)
  if (!containerElem) return

  try {
    const mermaid = await ensureMermaid()

    // First, validate the syntax with parse
    await mermaid.parse(code, { suppressErrors: true })

    // If parse succeeds, proceed with rendering
    const { svg } = await mermaid.render(`${id}-${Date.now()}`, code)

    // Create a wrapper for the SVG with responsive scaling
    const svgWrapper = document.createElement('div')
    svgWrapper.className = 'mermaid-svg-wrapper'
    svgWrapper.style.cssText =
      'width: 100%; height: auto; display: flex; justify-content: center; align-items: center;'

    // Parse and modify the SVG for responsive scaling
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svg, 'image/svg+xml')
    const svgElement = svgDoc.querySelector('svg')

    if (svgElement) {
      // Set responsive attributes
      svgElement.style.cssText =
        'max-width: 100%; max-height: 100%; margin: auto; display: block;'
      svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet')

      // If no viewBox exists, create one from width and height
      if (!svgElement.hasAttribute('viewBox')) {
        const width = svgElement.getAttribute('width') || '100%'
        const height = svgElement.getAttribute('height') || '100%'
        if (width !== '100%' && height !== '100%') {
          svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`)
        }
      }

      // Remove fixed width/height to make it responsive
      svgElement.removeAttribute('width')
      svgElement.removeAttribute('height')

      svgWrapper.innerHTML = sanitizeSvgOnly(svgElement.outerHTML)
    } else {
      // Fallback: use the original SVG string
      svgWrapper.innerHTML = sanitizeSvgOnly(svg)
    }

    containerElem.innerHTML = ''
    containerElem.appendChild(svgWrapper)
    addMermaidButtons(containerElem, code)

    renderCache.set(id, svgWrapper.outerHTML)
  } catch {
    // On error, show the raw mermaid code in a pre tag
    const escaped = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
    const fallbackResult = sanitizeHtmlOnly(`<pre class="mermaid-fallback"><code>${escaped}</code></pre>`)

    containerElem.innerHTML = fallbackResult
    renderCache.set(id, fallbackResult)
  }
}

const mermaidChart = (id: string, code: string) => {
  return `<pre id="${id}" class="mermaid" data-mermaid-code="${base64Encode(code)}"></pre>`
}

const MERMAID_FENCE_WRAPPED = Symbol('mermaidFenceWrapped')

const markdownItMermaid = (md: MarkdownIt, config?: MermaidConfig) => {
  if (config) {
    // Clear cache if config is changed to avoid stale renders
    if (!Object.is(_config, config)) {
      renderCache.clear()
      mermaidInstance = null
      mermaidInitPromise = null
      iconPackRegistered = false
    }

    // Merge user config with existing config
    _config = {
      ..._config,
      ...config,
    }
  }

  // @ts-ignore augment at runtime
  md.mermaid = {
    renderAll: async () => {
      const mermaidElems = Array.from(document.querySelectorAll<HTMLElement>('pre.mermaid'))
      const pendingElems = mermaidElems.filter((elem) => !elem.querySelector('svg'))
      if (!pendingElems.length) {
        return
      }

      for (const elem of pendingElems) {
        const id = elem.getAttribute('id')
        const code = elem.getAttribute('data-mermaid-code')
        if (!id || !code) {
          continue
        }

        const decodedCode = base64Decode(code)

        // If already rendered and cached, use the cached version
        const cachedResult = renderCache.get(id)
        if (cachedResult) {
          elem.innerHTML = sanitizeRichHtml(cachedResult)
          addMermaidButtons(elem, decodedCode)
          continue
        }

        // If not cached, render and cache it
        await renderAndCaching(id, decodedCode)
      }
    },
  }

  type FenceToken = {
    info?: string
    content: string
  }

  type RendererLike = {
    renderToken(tokens: FenceToken[], idx: number, options: unknown): string
  }

  type FenceRule = (
    tokens: FenceToken[],
    idx: number,
    options: unknown,
    env: unknown,
    self: RendererLike
  ) => string

  type MarkedFenceRule = FenceRule & {
    [MERMAID_FENCE_WRAPPED]?: true
  }

  const original: FenceRule = (md.renderer.rules.fence ||
    ((tokens, idx, options, _env, self) =>
      self.renderToken(tokens, idx, options as never))) as FenceRule

  if ((md.renderer.rules.fence as MarkedFenceRule | undefined)?.[MERMAID_FENCE_WRAPPED]) {
    return
  }

  const mermaidFenceRule: MarkedFenceRule = (tokens, idx, options, env, self) => {
    const token = tokens[idx]
    if (!token) {
      return original(tokens, idx, options, env, self)
    }
    if (token.info?.trim() === 'mermaid') {
      const code = token.content.trim()
      const id = getHashCodeId(code)
      const cachedResult = renderCache.get(id)
      return mermaidChart(id, cachedResult ?? code)
    }

    return original(tokens, idx, options, env, self)
  }

  mermaidFenceRule[MERMAID_FENCE_WRAPPED] = true
  md.renderer.rules.fence = mermaidFenceRule
}

export default markdownItMermaid
